import { checkAddress } from 'legacy/lut/helpers/validateRules/checkAddress'
import { checkAsianCityName } from 'legacy/lut/helpers/validateRules/checkAsianCityName'
import { checkCityName } from 'legacy/lut/helpers/validateRules/checkCityName'
import { checkAsianCompanyName, checkCompanyName } from 'legacy/lut/helpers/validateRules/partner'
import { useMemo } from 'react'

import { ValidatedByCountryFields } from 'clickNgo/configs/validation'
import {
  BasicInfoFields,
  LegalInfoFields,
  PartnerHierarchyType,
} from 'clickNgo/entities/partner/configs'
import { asianCountriesWithEspeciallyRequirements } from 'clickNgo/entities/partner/configs/common'
import { TPartnerFormKeys } from 'clickNgo/entities/partner/models/form/common'
import {
  IPartnerFormLegalRequiredFields,
  IPartnerFormLegalValidate,
} from 'clickNgo/entities/partner/models/form/legal'
import { useValidateByCountry } from 'clickNgo/hooks/validation'
import { useGetFormData, useGetFormDataField } from 'cng-common/features/form'

import { callValidateByDecision } from '../../../helpers/callValidateByDecision'

interface IUsePartnerBasicInfoValidationParams {
  formKeys: TPartnerFormKeys
}

export const usePartnerLegalInfoValidation = ({
  formKeys,
}: IUsePartnerBasicInfoValidationParams) => {
  const [{ modifiedFields }] = useGetFormData(formKeys.legal)
  const [legalCountry] = useGetFormDataField<string>(formKeys.legal, LegalInfoFields.LegalCountry)
  const [hierarchyType] = useGetFormDataField<PartnerHierarchyType>(
    formKeys.legal,
    BasicInfoFields.HierarchyType
  )

  const checkLegalCityNameByCountry = useMemo(
    () =>
      asianCountriesWithEspeciallyRequirements.includes(legalCountry)
        ? checkAsianCityName
        : checkCityName,
    [legalCountry]
  )

  const checkLegalCompanyNameByCountry = useMemo(
    () =>
      asianCountriesWithEspeciallyRequirements.includes(legalCountry)
        ? checkAsianCompanyName
        : checkCompanyName,
    [legalCountry]
  )

  const [onLegalStateValidate, isLegalStateByRulesRequired] = useValidateByCountry({
    countryName: legalCountry,
    field: ValidatedByCountryFields.STATE,
  })
  const [onLegalZipCodeValidate, isLegalZipCodeByRulesRequired] = useValidateByCountry({
    countryName: legalCountry,
    field: ValidatedByCountryFields.ZIP_CODE,
  })

  const isRequiredByHierarchyType = [
    PartnerHierarchyType.HEADQUARTERS,
    PartnerHierarchyType.AFFILIATE,
    PartnerHierarchyType.RESELLER_NETWORK,
  ].includes(hierarchyType)

  const isLegalAddressLine1Required =
    modifiedFields.includes(LegalInfoFields.LegalAddressLine1) && isRequiredByHierarchyType
  const isLegalCityRequired =
    modifiedFields.includes(LegalInfoFields.LegalCity) && isRequiredByHierarchyType
  const isLegalCountryRequired =
    modifiedFields.includes(LegalInfoFields.LegalCountry) && isRequiredByHierarchyType
  const isLegalStateRequired =
    (modifiedFields.includes(LegalInfoFields.LegalState) ||
      modifiedFields.includes(LegalInfoFields.LegalCountry)) &&
    isLegalStateByRulesRequired
  const isLegalZipCodeRequired =
    (modifiedFields.includes(LegalInfoFields.LegalZipCode) ||
      modifiedFields.includes(LegalInfoFields.LegalCountry)) &&
    isLegalZipCodeByRulesRequired

  const legalValidationProps = useMemo<IPartnerFormLegalValidate & IPartnerFormLegalRequiredFields>(
    () => ({
      onLegalAddressLine1Validate: callValidateByDecision(
        modifiedFields.includes(LegalInfoFields.LegalAddressLine1),
        checkAddress
      ),
      onLegalStateValidate: callValidateByDecision(
        modifiedFields.includes(LegalInfoFields.LegalState) ||
          modifiedFields.includes(LegalInfoFields.LegalCountry),
        onLegalStateValidate
      ),
      onLegalZipCodeValidate: callValidateByDecision(
        modifiedFields.includes(LegalInfoFields.LegalZipCode) ||
          modifiedFields.includes(LegalInfoFields.LegalCountry),
        onLegalZipCodeValidate
      ),
      onLegalCityValidate: callValidateByDecision(isLegalCityRequired, checkLegalCityNameByCountry),
      onCompanyLegalNameValidate: checkLegalCompanyNameByCountry,
      isLegalAddressLine1Required,
      isLegalCityRequired,
      isLegalCountryRequired,
      isLegalStateRequired,
      isLegalZipCodeRequired,
    }),
    [
      checkLegalCityNameByCountry,
      checkLegalCompanyNameByCountry,
      isLegalAddressLine1Required,
      isLegalCityRequired,
      isLegalCountryRequired,
      isLegalStateRequired,
      isLegalZipCodeRequired,
      modifiedFields,
      onLegalStateValidate,
      onLegalZipCodeValidate,
    ]
  )

  return { legalValidationProps }
}
